import React from 'react'
import './SignatureEvent.css'

export type SignatureEventImage = {
    src: string
    alt: string
}

export type SignatureEventCta = {
    href: string
    label: string
}

export type SignatureEventYoutube = {
    type: 'youtube'
    src: string
    title: string
}

export type SignatureEventLocalVideo = {
    type: 'local'
    src: string
    title?: string
}

export type SignatureEventVideo = SignatureEventYoutube | SignatureEventLocalVideo

type SignatureEventProps = {
    title: string
    description?: React.ReactNode
    meta?: React.ReactNode
    cta?: SignatureEventCta
    image?: SignatureEventImage
    videos?: SignatureEventVideo[]
    mediaSide?: 'left' | 'right'
}

const SignatureEvent: React.FC<SignatureEventProps> = ({
    title,
    description,
    meta,
    cta,
    image,
    videos = [],
    mediaSide = 'left',
}) => {
    const hasMedia = Boolean(image) || videos.length > 0

    return (
        <article className={`signature-event signature-event--media-${mediaSide}`}>
            <div className="signature-event__details">
                <h2 className="signature-event__title">{title}</h2>
                {description && <div className="signature-event__description">{description}</div>}
                {meta && <div className="signature-event__meta">{meta}</div>}
                {cta && (
                    <a
                        href={cta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-dark signature-event__cta"
                    >
                        {cta.label}
                    </a>
                )}
            </div>

            {hasMedia && (
                <div className="signature-event__media">
                    {image && (
                        <img
                            className="signature-event__image"
                            src={image.src}
                            alt={image.alt}
                            loading="lazy"
                        />
                    )}
                    {videos.map((video) =>
                        video.type === 'youtube' ? (
                            <div key={video.src} className="signature-event__embed">
                                <iframe
                                    src={video.src}
                                    title={video.title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                />
                            </div>
                        ) : (
                            <video
                                key={video.src}
                                className="signature-event__video"
                                controls
                                playsInline
                                preload="metadata"
                                aria-label={video.title}
                            >
                                <source src={video.src} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        )
                    )}
                </div>
            )}
        </article>
    )
}

export default SignatureEvent
